<template>
  <div class="admin-page">
    <div class="sidebar-holder">
      <aside class="admin-sidebar">
        <div class="sidebar-brand">
          <p class="sidebar-eyebrow">ADMIN</p>
          <h1>后台管理</h1>
          <p>定点医疗机构与定点药店数据维护</p>
        </div>

        <nav class="sidebar-menu">
          <button
            v-for="item in sourceOptions"
            :key="item.key"
            class="menu-item"
            :class="{ active: activeSource === item.key }"
            @click="switchSource(item.key)"
          >
            <span class="menu-icon" :style="{ background: item.color }"></span>
            <span class="menu-content">
              <strong>{{ item.label }}</strong>
              <small>{{ item.description }}</small>
            </span>
          </button>

          <router-link  class="menu-item menu-item-link menu-item-return" to="/">
            <span class="menu-icon menu-icon-back"></span>
            <span class="menu-content">
              <strong>返回前台</strong>
              <small>回到首页入口页</small>
            </span>
          </router-link>
        </nav>
      </aside>
    </div>

    <main class="admin-main">
      <header class="main-header">
        <div>
          <p class="main-eyebrow">{{ currentSourceMeta.label }}</p>
          <h2>{{ currentSourceMeta.title }}</h2>
          <p>{{ currentSourceMeta.description }}</p>
        </div>

        <button v-if="activeSource !== 'settings'" class="create-button" @click="openCreateDialog">
          新增{{ currentSourceMeta.shortLabel }}
        </button>
      </header>

      <section class="admin-stats">
        <article
          v-for="item in summaryItems"
          :key="item.key"
          class="stat-card"
        >
          <span class="stat-dot" :style="{ background: item.color }"></span>
          <div>
            <p class="stat-value">{{ item.count }}</p>
            <p class="stat-label">{{ item.label }}</p>
          </div>
        </article>
      </section>

      <section class="admin-panel">
        <template v-if="activeSource === 'settings'">
          <div class="settings-panel">
            <div
              v-for="item in settingRecords"
              :key="item.key"
              class="setting-item"
            >
              <div class="setting-item__info">
                <p class="setting-item__label">{{ getSettingLabel(item.key) }}</p>
                <p class="setting-item__key">{{ item.key }}</p>
              </div>
              <div class="setting-item__actions">
                <input v-model.trim="item.content" class="setting-input" type="number" min="1">
                <button class="action-button primary" :disabled="saving" @click="updateSetting(item)">
                  {{ saving ? '保存中...' : '保存' }}
                </button>
              </div>
            </div>
            <div v-if="!loading && !settingRecords.length" class="empty-cell settings-empty">
              <span v-if="errorMessage">{{ errorMessage }}</span>
              <span v-else>暂无配置</span>
            </div>
          </div>
        </template>

        <template v-else>
          <div class="admin-toolbar">
            <div class="filter-row filter-row-main">
              <input
                v-model.trim="keyword"
                class="search-input search-input-keyword"
                type="text"
                :placeholder="currentSourceMeta.searchPlaceholder"
                @keyup.enter="applyFilters"
              >

              <template v-if="activeSource === 'institutions'">
                <input
                  v-model.trim="selectedLevel"
                  class="search-input search-input-level"
                  type="text"
                  placeholder="搜索机构级别"
                  @keyup.enter="applyFilters"
                >

                <select v-model="selectedInstitutionType" class="select-field select-field-type" @change="applyFilters">
                  <option value="">全部类型</option>
                  <option
                    v-for="item in institutionTypeOptions"
                    :key="item"
                    :value="item"
                  >
                    {{ item }}
                  </option>
                </select>
              </template>

              <template v-else>
                <input
                  v-model.trim="selectedScope"
                  class="search-input search-input-level"
                  type="text"
                  placeholder="搜索开通范围"
                  @keyup.enter="applyFilters"
                >

                <select v-model="selectedStatus" class="select-field select-field-type" @change="applyFilters">
                  <option value="">全部状态</option>
                  <option value="营业中">营业中</option>
                  <option value="暂停中">暂停中</option>
                </select>
              </template>

              <div class="toolbar-actions">
                <button class="action-button primary" @click="applyFilters">
                  查询
                </button>
                <button class="action-button" @click="resetFilters">
                  重置
                </button>
              </div>
            </div>
          </div>

          <div class="table-wrap">
            <table class="point-table">
              <thead>
                <tr v-if="activeSource === 'institutions'">
                  <th>序号</th>
                  <th>机构名称</th>
                  <th>级别</th>
                  <th>类型</th>
                  <th>电话</th>
                  <th>地址</th>
                  <th>经纬度</th>
                  <th>操作</th>
                </tr>
                <tr v-else>
                  <th>序号</th>
                  <th>药店名称</th>
                  <th>开通范围</th>
                  <th>状态</th>
                  <th>电话</th>
                  <th>地址</th>
                  <th>经纬度</th>
                  <th>操作</th>
                </tr>
              </thead>

              <tbody v-if="!loading && records.length">
                <template v-if="activeSource === 'institutions'">
                  <tr
                    v-for="(item, index) in records"
                    :key="`institution-${item.id}`"
                  >
                    <td>{{ displaySequence(item, index) }}</td>
                    <td class="name-cell">{{ item.standard_name || '-' }}</td>
                    <td class="level-cell">{{ item.institution_level || '-' }}</td>
                    <td>{{ item.institution_type || '-' }}</td>
                    <td>{{ item.contact_phone || '-' }}</td>
                    <td class="address-cell">{{ item.address || '-' }}</td>
                    <td>{{ formatCoords(item.longitude, item.latitude) }}</td>
                    <td>
                      <div class="row-actions">
                        <button class="row-button edit" @click="openEditDialog(item)">
                          编辑
                        </button>
                        <button class="row-button delete" @click="removeRecord(item)">
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>

                <template v-else>
                  <tr
                    v-for="(item, index) in records"
                    :key="`pharmacy-${item.id}`"
                  >
                    <td>{{ displaySequence(item, index) }}</td>
                    <td class="name-cell">{{ item.standard_name || '-' }}</td>
                    <td>{{ item.medical_scope || '-' }}</td>
                    <td class="status-cell">{{ item.status || '-' }}</td>
                    <td>{{ item.contact_phone || '-' }}</td>
                    <td class="address-cell">{{ item.address || '-' }}</td>
                    <td>{{ formatCoords(item.longitude, item.latitude) }}</td>
                    <td>
                      <div class="row-actions">
                        <button class="row-button edit" @click="openEditDialog(item)">
                          编辑
                        </button>
                        <button class="row-button delete" @click="removeRecord(item)">
                          删除
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>

              <tbody v-else>
                <tr>
                  <td :colspan="activeSource === 'institutions' ? 8 : 8" class="empty-cell">
                    <span v-if="loading">正在加载数据...</span>
                    <span v-else-if="errorMessage">{{ errorMessage }}</span>
                    <span v-else>暂无数据</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="pager">
            <p class="pager-summary">
              第 {{ currentPage }} / {{ totalPages }} 页，共 {{ total }} 条
            </p>
            <div class="pager-actions">
              <button
                class="pager-button"
                :disabled="loading || currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                上一页
              </button>

              <button
                v-for="page in visiblePages"
                :key="page"
                class="pager-button page-number"
                :class="{ active: page === currentPage }"
                @click="goToPage(page)"
              >
                {{ page }}
              </button>

              <button
                class="pager-button"
                :disabled="loading || currentPage === totalPages"
                @click="goToPage(currentPage + 1)"
              >
                下一页
              </button>
            </div>
          </div>
        </template>
      </section>
    </main>

    <div v-if="dialogVisible" class="dialog-mask" @click.self="closeDialog">
      <div class="dialog-card">
        <div class="dialog-header">
          <div>
            <p class="dialog-eyebrow">{{ currentSourceMeta.label }}</p>
            <h3>{{ dialogMode === 'create' ? `新增${currentSourceMeta.shortLabel}` : `编辑${currentSourceMeta.shortLabel}` }}</h3>
          </div>
          <button class="dialog-close" @click="closeDialog">×</button>
        </div>

        <div class="dialog-form">
          <label class="form-item form-item-full">
            <span>{{ activeSource === 'institutions' ? '机构名称' : '药店名称' }}</span>
            <input v-model.trim="form.standard_name" type="text">
          </label>

          <template v-if="activeSource === 'institutions'">
            <label class="form-item">
              <span>机构级别</span>
              <input v-model.trim="form.institution_level" type="text">
            </label>

            <label class="form-item">
              <span>机构类型</span>
              <input v-model.trim="form.institution_type" type="text">
            </label>
          </template>

          <template v-else>
            <label class="form-item form-item-full">
              <span>医疗开通范围</span>
              <input v-model.trim="form.medical_scope" type="text">
            </label>

            <label class="form-item">
              <span>状态</span>
              <select v-model="form.status">
                <option value="营业中">营业中</option>
                <option value="暂停中">暂停中</option>
              </select>
            </label>
          </template>

          <label class="form-item form-item-full">
            <span>地址</span>
            <input v-model.trim="form.address" type="text">
          </label>

          <label class="form-item">
            <span>联系电话</span>
            <input v-model.trim="form.contact_phone" type="text">
          </label>

          <label class="form-item">
            <span>经度</span>
            <input v-model.number="form.longitude" type="number" step="0.000001">
          </label>

          <label class="form-item">
            <span>纬度</span>
            <input v-model.number="form.latitude" type="number" step="0.000001">
          </label>
        </div>

        <div class="dialog-actions">
          <button class="action-button" @click="closeDialog">取消</button>
          <button class="action-button primary" :disabled="saving" @click="submitForm">
            {{ saving ? '提交中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const SOURCE_META = {
  institutions: {
    label: '医疗机构',
    shortLabel: '机构',
    title: '定点医疗机构管理',
    description: '支持医疗机构的新增、编辑、删除、筛选与分页查询。',
    endpoint: '/api/admin/institutions',
    color: '#3a86ff',
    searchPlaceholder: '搜索机构名称'
  },
  pharmacies: {
    label: '药店',
    shortLabel: '药店',
    title: '定点药店管理',
    description: '支持药店的新增、编辑、删除、筛选与分页查询。',
    endpoint: '/api/admin/pharmacies',
    color: '#19b77f',
    searchPlaceholder: '搜索药店名称'
  },
  settings: {
    label: '系统设置',
    shortLabel: '配置',
    title: '地图运行参数',
    description: '编辑旗帜闪烁周期与空闲复位时间。',
    endpoint: '/api/admin/settings',
    color: '#7c5cff',
    searchPlaceholder: ''
  }
}

const SETTING_LABELS = {
  map_flag_blink_seconds: '旗帜闪烁周期（秒）',
  map_idle_reset_seconds: '空闲复位时间（秒）'
}

function createEmptyForm(source) {
  if (source === 'institutions') {
    return {
      id: null,
      standard_name: '',
      institution_level: '',
      institution_type: '',
      address: '',
      contact_phone: '',
      longitude: '',
      latitude: ''
    }
  }

  return {
    id: null,
    standard_name: '',
    medical_scope: '',
    status: '营业中',
    address: '',
    contact_phone: '',
    longitude: '',
    latitude: ''
  }
}

export default {
  name: 'AdminView',
  data() {
    return {
      activeSource: 'institutions',
      keyword: '',
      selectedLevel: '',
      selectedInstitutionType: '',
      selectedScope: '',
      selectedStatus: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      records: [],
      loading: false,
      saving: false,
      errorMessage: '',
      institutionTypeOptions: [],
      settingRecords: [],
      dialogVisible: false,
      dialogMode: 'create',
      form: createEmptyForm('institutions')
    }
  },
  computed: {
    sourceOptions() {
      return [
        {
          key: 'institutions',
          label: '医疗机构',
          description: '机构表维护',
          color: SOURCE_META.institutions.color
        },
        {
          key: 'pharmacies',
          label: '药店',
          description: '药店表维护',
          color: SOURCE_META.pharmacies.color
        },
        {
          key: 'settings',
          label: '系统设置',
          description: '地图参数维护',
          color: SOURCE_META.settings.color
        }
      ]
    },
    currentSourceMeta() {
      return SOURCE_META[this.activeSource]
    },
    totalPages() {
      if (this.activeSource === 'settings') {
        return 1
      }
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, start + 4)

      for (let page = start; page <= end; page += 1) {
        pages.push(page)
      }

      return pages
    },
    summaryItems() {
      if (this.activeSource === 'settings') {
        return [
          {
            key: 'source',
            color: this.currentSourceMeta.color,
            label: `当前模块：${this.currentSourceMeta.label}`,
            count: this.settingRecords.length
          },
          {
            key: 'blink',
            color: '#3a86ff',
            label: '闪烁周期配置项',
            count: this.settingRecords.find(item => item.key === 'map_flag_blink_seconds')?.content || '-'
          },
          {
            key: 'reset',
            color: '#ff8b3d',
            label: '复位时间配置项',
            count: this.settingRecords.find(item => item.key === 'map_idle_reset_seconds')?.content || '-'
          }
        ]
      }

      return [
        {
          key: 'source',
          color: this.currentSourceMeta.color,
          label: `当前模块：${this.currentSourceMeta.label}`,
          count: this.total
        },
        {
          key: 'page',
          color: '#7c5cff',
          label: '当前页码',
          count: this.currentPage
        },
        {
          key: 'pageSize',
          color: '#ff8b3d',
          label: '每页条数',
          count: this.pageSize
        }
      ]
    }
  },
  created() {
    this.fetchCurrentModule()
  },
  methods: {
    fetchCurrentModule() {
      if (this.activeSource === 'settings') {
        this.fetchSettings()
        return
      }

      this.fetchRecords()
    },
    async fetchRecords() {
      this.loading = true
      this.errorMessage = ''

      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize
        }

        if (this.keyword) {
          params.keyword = this.keyword
        }

        if (this.activeSource === 'institutions') {
          if (this.selectedLevel) {
            params.level = this.selectedLevel
          }

          if (this.selectedInstitutionType) {
            params.type = this.selectedInstitutionType
          }
        } else {
          if (this.selectedScope) {
            params.scope = this.selectedScope
          }

          if (this.selectedStatus) {
            params.status = this.selectedStatus
          }
        }

        const { data } = await axios.get(this.currentSourceMeta.endpoint, { params })
        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || '接口请求失败')
        }

        const payload = data.data || {}
        this.records = Array.isArray(payload.list) ? payload.list : []
        this.total = Number(payload.total || 0)
        this.currentPage = Number(payload.page || this.currentPage)
        this.pageSize = Number(payload.page_size || this.pageSize)
        this.rebuildOptions()
      } catch (error) {
        this.records = []
        this.total = 0
        this.errorMessage = error.response?.data?.message || error.message || '数据加载失败'
      } finally {
        this.loading = false
      }
    },
    async fetchSettings() {
      this.loading = true
      this.errorMessage = ''

      try {
        const { data } = await axios.get(SOURCE_META.settings.endpoint)
        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || '配置加载失败')
        }

        const payload = data.data || {}
        this.settingRecords = Array.isArray(payload.list) ? payload.list : []
        this.total = this.settingRecords.length
        this.currentPage = 1
      } catch (error) {
        this.settingRecords = []
        this.total = 0
        this.errorMessage = error.response?.data?.message || error.message || '配置加载失败'
      } finally {
        this.loading = false
      }
    },
    rebuildOptions() {
      if (this.activeSource === 'institutions') {
        this.institutionTypeOptions = [...new Set(
          this.records
            .map(item => item.institution_type)
            .filter(Boolean)
        )]
      }
    },
    switchSource(source) {
      if (this.activeSource === source) {
        return
      }

      this.activeSource = source
      this.keyword = ''
      this.selectedLevel = ''
      this.selectedInstitutionType = ''
      this.selectedScope = ''
      this.selectedStatus = ''
      this.currentPage = 1
      this.total = 0
      this.records = []
      this.settingRecords = []
      this.errorMessage = ''
      this.dialogVisible = false
      this.form = createEmptyForm(source)
      this.fetchCurrentModule()
    },
    applyFilters() {
      if (this.activeSource === 'settings') {
        return
      }
      this.currentPage = 1
      this.fetchRecords()
    },
    resetFilters() {
      if (this.activeSource === 'settings') {
        return
      }
      this.keyword = ''
      this.selectedLevel = ''
      this.selectedInstitutionType = ''
      this.selectedScope = ''
      this.selectedStatus = ''
      this.currentPage = 1
      this.fetchRecords()
    },
    goToPage(page) {
      const nextPage = Math.min(this.totalPages, Math.max(1, page))
      if (nextPage === this.currentPage) {
        return
      }

      this.currentPage = nextPage
      this.fetchRecords()
    },
    openCreateDialog() {
      if (this.activeSource === 'settings') {
        return
      }
      this.dialogMode = 'create'
      this.form = createEmptyForm(this.activeSource)
      this.dialogVisible = true
    },
    openEditDialog(item) {
      this.dialogMode = 'edit'
      this.form = this.activeSource === 'institutions'
        ? {
            id: item.id,
            standard_name: item.standard_name || '',
            institution_level: item.institution_level || '',
            institution_type: item.institution_type || '',
            address: item.address || '',
            contact_phone: item.contact_phone || '',
            longitude: item.longitude,
            latitude: item.latitude
          }
        : {
            id: item.id,
            standard_name: item.standard_name || '',
            medical_scope: item.medical_scope || '',
            status: item.status || '营业中',
            address: item.address || '',
            contact_phone: item.contact_phone || '',
            longitude: item.longitude,
            latitude: item.latitude
          }
      this.dialogVisible = true
    },
    closeDialog() {
      if (this.saving) {
        return
      }

      this.dialogVisible = false
      this.form = createEmptyForm(this.activeSource)
    },
    buildPayload() {
      const basePayload = {
        standard_name: this.form.standard_name,
        address: this.form.address,
        contact_phone: this.form.contact_phone,
        longitude: Number(this.form.longitude || 0),
        latitude: Number(this.form.latitude || 0)
      }

      if (this.activeSource === 'institutions') {
        return {
          ...basePayload,
          institution_level: this.form.institution_level,
          institution_type: this.form.institution_type
        }
      }

      return {
        ...basePayload,
        medical_scope: this.form.medical_scope,
        status: this.form.status
      }
    },
    async submitForm() {
      if (!this.form.standard_name) {
        this.errorMessage = `${this.currentSourceMeta.shortLabel}名称不能为空`
        return
      }

      this.saving = true
      this.errorMessage = ''

      try {
        const payload = this.buildPayload()
        if (this.dialogMode === 'create') {
          const { data } = await axios.post(this.currentSourceMeta.endpoint, payload)
          if (!data || data.code !== 0) {
            throw new Error((data && data.message) || '新增失败')
          }
        } else {
          const { data } = await axios.put(`${this.currentSourceMeta.endpoint}/${this.form.id}`, payload)
          if (!data || data.code !== 0) {
            throw new Error((data && data.message) || '更新失败')
          }
        }

        this.closeDialog()
        this.fetchRecords()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || error.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    getSettingLabel(key) {
      return SETTING_LABELS[key] || key
    },
    async updateSetting(item) {
      const value = String(item.content || '').trim()
      if (!value) {
        this.errorMessage = `${this.getSettingLabel(item.key)}不能为空`
        return
      }

      this.saving = true
      this.errorMessage = ''

      try {
        const { data } = await axios.put(`${SOURCE_META.settings.endpoint}/${item.key}`, {
          content: value
        })
        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || '配置保存失败')
        }
        this.fetchSettings()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || error.message || '配置保存失败'
      } finally {
        this.saving = false
      }
    },
    async removeRecord(item) {
      const ok = window.confirm(`确认删除“${item.standard_name || '当前记录'}”吗？`)
      if (!ok) {
        return
      }

      try {
        const { data } = await axios.delete(`${this.currentSourceMeta.endpoint}/${item.id}`)
        if (!data || data.code !== 0) {
          throw new Error((data && data.message) || '删除失败')
        }

        if (this.records.length === 1 && this.currentPage > 1) {
          this.currentPage -= 1
        }
        this.fetchRecords()
      } catch (error) {
        this.errorMessage = error.response?.data?.message || error.message || '删除失败'
      }
    },
    formatCoords(longitude, latitude) {
      if (longitude === null || longitude === undefined || latitude === null || latitude === undefined) {
        return '-'
      }

      const lng = Number(longitude)
      const lat = Number(latitude)
      if (Number.isNaN(lng) || Number.isNaN(lat)) {
        return '-'
      }

      return `${lng.toFixed(6)}, ${lat.toFixed(6)}`
    },
    displaySequence(item, index) {
      if (item && item.id) {
        return item.id
      }

      return (this.currentPage - 1) * this.pageSize + index + 1
    }
  }
}
</script>

<style>
.admin-page {
  min-height: 100%;
  display: grid;
  grid-template-columns: 220px minmax(0, 1fr);
  align-items: start;
  gap: 18px;
  padding: 18px;
  background: linear-gradient(180deg, #f4f8ff 0%, #eef4fb 100%);
}

.sidebar-holder {
  position: sticky;
  top: 18px;
  align-self: start;
  height: fit-content;
}

.admin-sidebar {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px 16px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.92);
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 18px 40px rgba(16, 38, 63, 0.08);
}

.sidebar-brand {
  padding: 2px 2px 0;
}

.sidebar-eyebrow {
  margin: 0 0 10px;
  color: #7c95b3;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
}

.sidebar-brand h1 {
  margin: 0 0 6px;
  color: #10263f;
  font-size: 24px;
  line-height: 1.08;
}

.sidebar-brand p {
  margin: 0;
  color: #607289;
  font-size: 13px;
  line-height: 1.65;
}

.sidebar-menu {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  border: 1px solid rgba(92, 123, 158, 0.1);
  border-radius: 18px;
  background: linear-gradient(180deg, #fbfdff 0%, #f3f8ff 100%);
  color: #10263f;
  text-align: left;
  cursor: pointer;
  transition: 0.2s ease;
  text-decoration: none;
}

.menu-item:hover {
  border-color: rgba(63, 134, 255, 0.18);
  box-shadow: 0 12px 20px rgba(47, 129, 255, 0.08);
}

.menu-item.active {
  border-color: rgba(63, 134, 255, 0.22);
  background: linear-gradient(135deg, #e9f2ff, #f7fbff);
  box-shadow: 0 14px 28px rgba(47, 129, 255, 0.12);
}

.menu-item-return {
  margin-top: 0;
}

.menu-item-return:hover {
  border-color: rgba(63, 134, 255, 0.18);
  box-shadow: 0 12px 20px rgba(47, 129, 255, 0.08);
}

.menu-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-shadow: 0 0 0 6px rgba(63, 134, 255, 0.08);
  flex-shrink: 0;
}

.menu-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.menu-content strong {
  font-size: 14px;
}

.menu-content small {
  color: #71839b;
  font-size: 12px;
}

.admin-main {
  min-width: 0;
  padding: 4px 6px 8px 0;
}

.menu-icon-back {
  background: linear-gradient(135deg, #3f8bff, #5ea6ff);
}

.main-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
}

.main-eyebrow {
  margin: 0 0 8px;
  color: #54779c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
}

.main-header h2 {
  margin: 0 0 10px;
  font-size: 40px;
  line-height: 1.08;
  color: #10263f;
}

.main-header p {
  margin: 0;
  color: #607289;
}

.create-button {
  min-width: 132px;
  height: 44px;
  padding: 0 18px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(135deg, #2f81ff, #5aa0ff);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(47, 129, 255, 0.18);
}

.admin-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.stat-card,
.admin-panel,
.dialog-card {
  border: 1px solid rgba(255, 255, 255, 0.88);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 18px 40px rgba(16, 38, 63, 0.08);
  backdrop-filter: blur(12px);
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 22px;
}

.stat-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.75);
}

.stat-value {
  margin: 0 0 4px;
  font-size: 28px;
  font-weight: 700;
  color: #10263f;
}

.stat-label {
  margin: 0;
  color: #607289;
}

.admin-panel {
  padding: 22px;
  border-radius: 26px;
}

.admin-toolbar {
  margin-bottom: 18px;
}

.filter-row,
.row-actions,
.pager-actions,
.dialog-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-row-main {
  flex-wrap: nowrap;
  align-items: stretch;
}

.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-shrink: 0;
}

.search-input,
.select-field,
.action-button,
.pager-button,
.row-button,
.dialog-form input,
.dialog-form select {
  height: 42px;
  border-radius: 999px;
  font-size: 14px;
}

.search-input,
.select-field,
.dialog-form input,
.dialog-form select {
  padding: 0 16px;
  border: 1px solid rgba(96, 114, 137, 0.16);
  background: #fff;
  color: #10263f;
  outline: none;
}

.search-input {
  min-width: 220px;
  flex: 1 1 0;
}

.search-input-keyword {
  flex: 1.3 1 0;
}

.search-input-level {
  flex: 0.95 1 0;
}

.select-field {
  min-width: 180px;
}

.select-field-type {
  min-width: 220px;
  flex: 0.75 0 220px;
}

.action-button,
.pager-button,
.row-button,
.dialog-close {
  border: 0;
  background: #edf3fb;
  color: #4d6783;
  font-weight: 700;
  cursor: pointer;
}

.action-button,
.pager-button {
  padding: 0 16px;
}

.action-button.primary,
.pager-button.active,
.row-button.edit {
  background: linear-gradient(135deg, #2f81ff, #5aa0ff);
  color: #fff;
}

.row-button.delete {
  background: #ffe6e6;
  color: #d64b4b;
}

.settings-panel {
  display: grid;
  gap: 16px;
}

.setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding: 18px 20px;
  border: 1px solid rgba(96, 114, 137, 0.12);
  border-radius: 22px;
  background: linear-gradient(180deg, #fbfdff 0%, #f3f8ff 100%);
}

.setting-item__info {
  min-width: 0;
}

.setting-item__label {
  margin: 0 0 6px;
  color: #10263f;
  font-size: 16px;
  font-weight: 700;
}

.setting-item__key {
  margin: 0;
  color: #71839b;
  font-size: 12px;
  letter-spacing: 0.04em;
}

.setting-item__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.setting-input {
  width: 160px;
  height: 42px;
  padding: 0 16px;
  border: 1px solid rgba(96, 114, 137, 0.16);
  border-radius: 999px;
  background: #fff;
  color: #10263f;
  font-size: 14px;
  outline: none;
}

.settings-empty {
  padding: 24px 12px 8px;
}

.table-wrap {
  overflow: auto;
  margin-bottom: 18px;
}

.point-table {
  width: 100%;
  border-collapse: collapse;
}

.point-table th,
.point-table td {
  padding: 14px 12px;
  border-bottom: 1px solid rgba(96, 114, 137, 0.12);
  text-align: left;
  vertical-align: top;
}

.point-table th {
  color: #607289;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.point-table td {
  color: #10263f;
  font-size: 14px;
}

.name-cell {
  min-width: 240px;
  white-space: normal;
}

.level-cell {
  min-width: 132px;
  white-space: normal;
}

.status-cell {
  min-width: 96px;
  white-space: nowrap;
}

.address-cell {
  min-width: 260px;
  white-space: normal;
}

.empty-cell {
  padding: 42px 12px;
  text-align: center !important;
  color: #607289 !important;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.pager-summary {
  margin: 0;
  color: #607289;
  font-size: 13px;
  font-weight: 700;
}

.pager-button:disabled {
  opacity: 0.48;
  cursor: not-allowed;
}

.dialog-mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(10, 22, 36, 0.34);
  z-index: 50;
}

.dialog-card {
  width: min(860px, 100%);
  padding: 22px;
  border-radius: 28px;
}

.dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
}

.dialog-eyebrow {
  margin: 0 0 8px;
  color: #54779c;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
}

.dialog-header h3 {
  margin: 0;
  color: #10263f;
  font-size: 28px;
}

.dialog-close {
  width: 40px;
  font-size: 22px;
}

.dialog-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-item span {
  color: #607289;
  font-size: 13px;
  font-weight: 700;
}

.form-item-full {
  grid-column: 1 / -1;
}

.dialog-actions {
  justify-content: flex-end;
  margin-top: 18px;
}

@media (max-width: 1180px) {
  .admin-page {
    grid-template-columns: 1fr;
    padding: 16px;
  }
}

@media (max-width: 960px) {
  .admin-main {
    padding: 18px;
  }

  .main-header {
    flex-direction: column;
  }

  .main-header h2 {
    font-size: 32px;
  }

  .admin-stats {
    grid-template-columns: 1fr;
  }

  .filter-row-main {
    flex-wrap: wrap;
  }

  .toolbar-actions {
    width: 100%;
    margin-left: 0;
    justify-content: flex-end;
  }

  .search-input,
  .select-field,
  .select-field-type {
    min-width: 100%;
    flex: 1 1 100%;
  }

  .dialog-form {
    grid-template-columns: 1fr;
  }

  .setting-item,
  .setting-item__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .setting-input {
    width: 100%;
  }
}
</style>
